import React, { useEffect, useState } from 'react';
import AppCard from '@projectK/components/AppCard';
import AppAnimate from '@projectK/components/AppAnimate';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import AppInfoView from '@projectK/components/AppInfoView';
import AppLoader from '@projectK/components/AppLoader';
import jwtAxios from '@projectK/services/auth/JWT';
import { Button, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BannerUpload from './BannerUpload';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useRolePermissions, hasPermission } from '@projectK/context/RolesProvider';
import ArrowCircleLeftOutlinedIcon from '@mui/icons-material/ArrowCircleLeftOutlined';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import {
  TournamentView,
  beginTournamentRegistrationWhileLoggedIn,
  continueNewAccountTournamentRegistration,
  exitTournamentRegistrationToListAsNewAccount,
  exitTournamentRegistrationWizard,
  isNewAccountTournamentRegistrationFlow,
  isTournamentRegistrationActive,
  isTournamentRegistrationHideNavActive,
  markTournamentRegistrationStartedLoggedOut,
} from '@projectK/modules/ecommerce/TournamentDetail';

const TournamentDetail = ({ slug }) => {
  const { productId } = useParams();
  const { user } = useAuthUser();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0); // Steps now: 0 = Sign Up, 1 = Team, 2 = Payment
  const [showDetails, setShowDetails] = useState(
    !isTournamentRegistrationActive(),
  );
  const [isPaymentOptionSkipped, setIsPaymentOptionSkipped] = useState(false); // For pre-step 0
  const companyTheme = CompanyTheme();
  const navigate = useNavigate();
  const { rolePermissions } = useRolePermissions();
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isTournamentRegistrationActive()) {
      setShowDetails(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    if (isNewAccountTournamentRegistrationFlow()) {
      return;
    }
    if (isTournamentRegistrationActive()) {
      beginTournamentRegistrationWhileLoggedIn();
    }
  }, [user?.id, slug, productId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Make a GET request using jwtAxios
      const response = await jwtAxios.get(
        !user ? `/public/tournament/${slug || productId}` : `/tournament/${slug || productId}`,
      );
      const { data } = response;

      setList(data);
      setLoading(false);
    } catch (error) {
      console.error('An error occurred:', error);
      setLoading(false);
    }
  };
  const handleBack = () => {
    if (showDetails) {
      if (isNewAccountTournamentRegistrationFlow()) {
        exitTournamentRegistrationToListAsNewAccount();
      } else {
        exitTournamentRegistrationWizard();
      }
      return navigate('/tournaments');
    }
    // If on the first step for logged-in or guest user, go to details
    if ((user?.id && activeStep === 1) || (!user?.id && activeStep === 0)) {
      setShowDetails(true);
      return;
    }
    if (isPaymentOptionSkipped && activeStep === 3) {
      setActiveStep((prev) => prev - 2);
      setIsPaymentOptionSkipped(false);
      return;
    }

    // Otherwise, go one step back
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleRegister = () => {
    if (!user?.id) {
      setActiveStep(0);
      markTournamentRegistrationStartedLoggedOut();
    } else if (isTournamentRegistrationHideNavActive()) {
      continueNewAccountTournamentRegistration();
    } else {
      beginTournamentRegistrationWhileLoggedIn();
    }
    setShowDetails(false);
  };

  const [divisions, setDivisions] = useState([]);
  const fetchPurchaseData = async () => {
    try {
      // Make a GET request using jwtAxios
      const response = await jwtAxios.get(
        !user
          ? `/public/tournament-division/tournament/${list.id}`
          : `/tournament-division/tournament/${list.id}`,
        {
          params: {
            showall: false,
            perPage: 'all',
            filters: {
              is_expired: 'false',
            },
          },
        },
      );
      const { data } = response;
      setDivisions(data.list);
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };
  useEffect(() => {
    if (list.id) {
      fetchPurchaseData();
    }
  }, [list.id]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  };
  
  const displaySettings = list?.displaySettings;
  const backgroundImageUrl = displaySettings?.tournament_banner
    ? `${process.env.NX_CLOUD_FRONT_URL}/${displaySettings.tournament_banner}`
    : '/assets/images/default-backgroud.png';
  return (
    <>
      {loading ? (
        <AppLoader />
      ) : (
        <div className='p-details'>
          <IconButton
            className='icon-btn icon-btn-eye back-btn'
            sx={{
              color: companyTheme ? companyTheme.primary.main : 'text.primary',
            }}
            onClick={() => {handleBack()}}
          >
            <ArrowCircleLeftOutlinedIcon fontSize='medium' />
          </IconButton>
          {!slug && (
            <div style={{ position: 'relative' }}>
              <div className='p-detail-bg'>
                <img src={backgroundImageUrl} alt='background' />
              </div>
            {hasPermission(rolePermissions, 'Tournaments - Tournaments setup', 'update') && (
                <Button
                  variant='contained'
                  color='primary'
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    padding: '2px 6px',
                    fontSize: '0.7rem',
                    minHeight: '24px',
                    height: 'auto',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                    },
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? companyTheme.primary.dark
                        : '#1976d2',
                    },
                  }}
                  className='edit-icon'
                  onClick={handleEditClick}
                >
                  <IconButton
                    size='small'
                    className='icon-btn icon-btn-eye'
                    sx={{ color: 'white' }}
                  >
                    <EditIcon />
                  </IconButton>
                  Update Image
                </Button>
              )}
            </div>
          )}
          <AppAnimate animation='transition.slideUpIn' delay={200}>
            <AppCard className='p-in-details' sx={{ marginBottom: '10px' }}>
              <TournamentView
                tournament={list}
                divisions={divisions}
                fetchData={fetchData}
                slug={slug}
                user={user}
                showDetails={showDetails}
                handleRegister={handleRegister}
                handleBack={handleBack}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                setIsPaymentOptionSkipped={setIsPaymentOptionSkipped}
              />
            </AppCard>
          </AppAnimate>
        </div>
      )}
      {isEditModalOpen && (
        <BannerUpload
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          settings={list}
          tournament_Id={list?.id}
          fetchData={fetchData}
          selectedTheme={companyTheme}
        />
      )}

      <AppInfoView />
    </>
  );
};

export default TournamentDetail;

TournamentDetail.propTypes = {
  slug: PropTypes.string,
};
